import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../../../libs/shared/core/src/services/tracking/tracking';
import {Button} from '../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {TooltipCoordinator} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {useAppDispatch, useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {ThemedImageNamesEnum} from '../../../../../helpers/imageThemeHelpers';
import {isDemoInbox} from '../../../../../helpers/modelHelpers';
import {SettingsResourcePagesEnum, SettingsRootPagesEnum} from '../../../../../models/url/models/types';
import {buildChannelPath} from '../../../../../models/url/settingsUrlHelpers';
import {getUserParticipatingInboxes} from '../../../../../store/data/inboxes/inboxesSelectors';
import {navigateToLinkAction} from '../../../../../store/stateless/navigationActions';
import {useOnboardingModalState} from '../../../../app/topBar/onboarding/onboardingContext';
import {ThemedImage} from '../../../../common/images/themedImage';
import {connectChannelTaskDetails} from '../../../../onboarding/admin/tasks/adminOnboardingText';

/*
 * Style.
 */

export const StyledEmptyStateGridDiv = styled.div`
  display: grid;
  grid-template-areas:
    'image'
    'text'
    'buttons';
  grid-template-rows: auto max-content max-content;
  width: 380px;
`;

export const StyledEmptyStateImg = styled(ThemedImage)`
  grid-area: image;
`;

const StyledTextDiv = styled.div`
  grid-area: text;

  display: flex;
  flex-direction: column;
  max-width: 300px;
  margin: auto;

  margin-top: 32px;

  color: ${(p) => p.theme.greys.shade80};
`;

export const StyledTitleDiv = styled.div`
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.veryLarge};

  margin-bottom: 10px;
  text-align: center;
`;

export const StyledDescriptionDiv = styled.div`
  font-weight: ${fontWeights.normal};
  font-size: ${fontSizes.medium};

  text-align: center;

  color: ${(p) => p.theme.greys.shade70};
  line-height: 21px;
`;

export const StyledButtonsDiv = styled.div`
  grid-area: buttons;

  display: flex;
  gap: 16px;
  margin: auto;

  padding-top: 16px;
`;

const StyledDetailsTooltipCoordinator = styled(TooltipCoordinator)`
  display: inline-block;
  margin-left: 4px;
  margin-bottom: -2px;
`;

/*
 * Props.
 */

interface TeamInboxEmptyStateProps {
  namespace?: string;
  inboxId: number | undefined;
}

/*
 * Component.
 */

export const TeamInboxEmptyState: FC<TeamInboxEmptyStateProps> = ({namespace, inboxId}) => {
  const dispatch = useAppDispatch();
  const inboxes = useAppSelector(getUserParticipatingInboxes);

  const hasRealInbox = inboxes.some((inbox) => !isDemoInbox(inbox));

  const {setIsAdminWizardOpen} = useOnboardingModalState();

  const regularDescription = (
    <Trans id="teaminbox-emptystate-description-connect-channel">
      See and manage real messages with your team by connecting a channel like SMS, Facebook, Front Chat, and
      more.
    </Trans>
  );

  function getEmptyStateContent() {
    if (hasRealInbox) {
      return {
        illustration: <StyledEmptyStateImg image={ThemedImageNamesEnum.INBOX_CONNECT_CHANNELS} />,
        title: (
          <>
            <Trans id="teaminbox-emptystate-title-explore">Import team messages into Front</Trans>
            <StyledDetailsTooltipCoordinator
              render={() => connectChannelTaskDetails}
              maxWidth={299}
              placement="top"
            />
          </>
        ),
      };
    }

    return {
      title: <Trans id="teaminbox-empty-state-title-setup-inbox">Get your shared inbox ready</Trans>,
      illustration: <StyledEmptyStateImg image={ThemedImageNamesEnum.INBOX_CREATE_INBOX} />,
      description: (
        <Trans id="teaminbox-empty-state-description-setup-inbox">
          Design your Front experience by creating your first shared inbox and workflows.
        </Trans>
      ),
      button: (
        <Button
          variant={ButtonVariantsEnum.PRIMARY}
          size={VisualSizesEnum.MEDIUM}
          onClick={() => setIsAdminWizardOpen(true)}
        >
          <Trans id="teaminbox-empty-state-button-setup-inbox">Set up shared inbox</Trans>
        </Button>
      ),
    };
  }

  const {title, description, button, illustration} = getEmptyStateContent();

  return (
    <StyledEmptyStateGridDiv>
      {illustration}
      <StyledTextDiv>
        <StyledTitleDiv>{title}</StyledTitleDiv>
        <StyledDescriptionDiv>{description ?? regularDescription}</StyledDescriptionDiv>
      </StyledTextDiv>
      <StyledButtonsDiv>
        {button ?? (
          <Button
            variant={ButtonVariantsEnum.PRIMARY}
            onClick={() => {
              trackInterfaceEvent(InterfaceEventsEnum.EMPTY_STATE_CONNECT_CHANNELS_CLICK);

              if (!namespace) {
                return;
              }
              dispatch(
                navigateToLinkAction(
                  buildChannelPath({
                    rootPage: SettingsRootPagesEnum.CHANNELS,
                    namespace,
                    resourcePage: SettingsResourcePagesEnum.NEW,
                    inboxId,
                  }),
                ),
              );
            }}
            size={VisualSizesEnum.MEDIUM}
          >
            <Trans id="teaminbox-emptystate-channels">Connect channels</Trans>
          </Button>
        )}
      </StyledButtonsDiv>
    </StyledEmptyStateGridDiv>
  );
};
